import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../services/api.service';
import { ToastService } from '../../services/toast.service';
import { ConfirmService } from '../../services/confirm.service';
import { Appuntamento, Cliente } from '../../models';

interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  dateString: string; // YYYY-MM-DD
  events: Appuntamento[];
}

@Component({
  selector: 'app-appuntamenti',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="page-container">
      
      <!-- Top Header & View Mode Selector -->
      <div class="header-flex">
        <div>
          <h1>Gestione Appuntamenti & Agenda</h1>
          <p class="subtitle">Programmazione interventi, cantieri e sopralluoghi</p>
        </div>

        <div class="actions-group">
          <!-- View Toggle Pills -->
          <div class="view-toggle">
            <button class="toggle-btn" [class.active]="viewMode === 'calendar'" (click)="viewMode = 'calendar'">
              <span class="material-icons">calendar_month</span> Calendario
            </button>
            <button class="toggle-btn" [class.active]="viewMode === 'list'" (click)="viewMode = 'list'">
              <span class="material-icons">format_list_bulleted</span> Lista
            </button>
          </div>

          <button class="btn-primary" (click)="openModal()">
            <span class="material-icons">add</span> Nuovo Appuntamento
          </button>
        </div>
      </div>

      <!-- VISTA CALENDARIO -->
      <div *ngIf="viewMode === 'calendar'" class="calendar-view card">
        <div class="card-swatch-top"></div>
        
        <!-- Calendar Header Navigation -->
        <div class="calendar-header">
          <div class="month-nav">
            <button class="btn-nav" (click)="prevMonth()" title="Mese precedente">
              <span class="material-icons">chevron_left</span>
            </button>
            <h2>{{ currentMonthDate | date:'MMMM yyyy':'':'it' }}</h2>
            <button class="btn-nav" (click)="nextMonth()" title="Mese successivo">
              <span class="material-icons">chevron_right</span>
            </button>
          </div>
          <button class="btn-today" (click)="goToToday()">Oggi</button>
        </div>

        <!-- Calendar Days Grid -->
        <div class="calendar-grid">
          <!-- Weekday Headers -->
          <div class="weekday-header" *ngFor="let w of weekDays">{{ w }}</div>

          <!-- Calendar Days -->
          <div 
            *ngFor="let day of calendarDays" 
            class="calendar-day-cell"
            [class.other-month]="!day.isCurrentMonth"
            [class.is-today]="day.isToday"
            [class.selected-day]="selectedDay?.dateString === day.dateString"
            (click)="selectDay(day)"
          >
            <div class="day-number-bar">
              <span class="day-number">{{ day.date.getDate() }}</span>
              <button class="btn-quick-add" (click)="openModalForDate(day.dateString, $event)" title="Aggiungi qui">
                <span class="material-icons">add</span>
              </button>
            </div>

            <div class="day-events-list">
              <div 
                *ngFor="let ev of day.events" 
                class="event-chip" 
                [ngClass]="'chip-' + ev.stato"
                (click)="editApp(ev); $event.stopPropagation()"
                [title]="ev.titolo + ' (' + ev.ora_inizio + ')'"
              >
                <span class="event-time">{{ ev.ora_inizio }}</span>
                <span class="event-title">{{ ev.titolo }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Selected Day Summary Panel -->
        <div class="selected-day-panel" *ngIf="selectedDay">
          <div class="panel-header">
            <h3>Appuntamenti del {{ selectedDay.date | date:'EEEE d MMMM yyyy':'':'it' }}</h3>
            <button class="btn-primary btn-sm-add" (click)="openModalForDate(selectedDay.dateString)">
              <span class="material-icons">add</span> Aggiungi Appuntamento
            </button>
          </div>

          <div class="events-cards-list" *ngIf="selectedDay.events.length > 0; else noEvents">
            <div class="event-detail-card" *ngFor="let ev of selectedDay.events">
              <div class="ev-time-badge">
                <span class="material-icons">schedule</span>
                <span>{{ ev.ora_inizio }} - {{ ev.ora_fine }}</span>
              </div>
              <div class="ev-main-info">
                <h4>{{ ev.titolo }}</h4>
                <p *ngIf="ev.cognome">👤 Cliente: <strong>{{ ev.cognome }} {{ ev.nome }}</strong></p>
                <p *ngIf="ev.luogo">📍 Luogo: {{ ev.luogo }}</p>
                <p *ngIf="ev.descrizione" class="ev-desc">📝 {{ ev.descrizione }}</p>
              </div>
              <div class="ev-actions">
                <span class="badge" [ngClass]="'badge-' + ev.stato">{{ ev.stato }}</span>
                <button class="btn-sm" (click)="editApp(ev)" title="Modifica"><span class="material-icons">edit</span></button>
                <button class="btn-sm btn-danger" (click)="deleteApp(ev.id!)" title="Elimina"><span class="material-icons">delete</span></button>
              </div>
            </div>
          </div>

          <ng-template #noEvents>
            <p class="empty-msg">Nessun appuntamento in programma per questa data.</p>
          </ng-template>
        </div>

      </div>

      <!-- VISTA LISTA -->
      <div *ngIf="viewMode === 'list'">
        <!-- Search Bar -->
        <div class="search-bar card">
          <span class="material-icons search-icon">search</span>
          <input type="text" [(ngModel)]="searchTerm" placeholder="Cerca per titolo, cliente o luogo..." />
          <button *ngIf="searchTerm" class="btn-clear" (click)="searchTerm = ''">Cancella</button>
        </div>

        <!-- Desktop Table View -->
        <div class="card table-card desktop-only">
          <table class="data-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Orario</th>
                <th>Titolo / Descrizione</th>
                <th>Luogo</th>
                <th>Cliente</th>
                <th>Stato</th>
                <th style="text-align: right;">Azioni</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let a of filteredAppuntamenti">
                <td><strong>{{ a.data | date:'dd/MM/yyyy' }}</strong></td>
                <td>{{ a.ora_inizio }} - {{ a.ora_fine }}</td>
                <td>
                  <div style="font-weight: 600; color: var(--text-main);">{{ a.titolo }}</div>
                  <div class="desc" style="font-size: 12px; color: var(--text-muted);">{{ a.descrizione }}</div>
                </td>
                <td>{{ a.luogo || '-' }}</td>
                <td>{{ a.cognome ? (a.cognome + ' ' + a.nome) : '-' }}</td>
                <td>
                  <span class="badge" [ngClass]="'badge-' + a.stato">{{ a.stato }}</span>
                </td>
                <td style="text-align: right;">
                  <button class="btn-sm" (click)="editApp(a)" title="Modifica">
                    <span class="material-icons" style="font-size: 16px;">edit</span>
                  </button>
                  <button class="btn-sm btn-danger" (click)="deleteApp(a.id!)" title="Elimina">
                    <span class="material-icons" style="font-size: 16px;">delete</span>
                  </button>
                </td>
              </tr>
              <tr *ngIf="filteredAppuntamenti.length === 0">
                <td colspan="7" class="text-center">Nessun appuntamento trovato.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Mobile Card View -->
        <div class="mobile-only mobile-card-list">
          <div class="mobile-item-card card" *ngFor="let a of filteredAppuntamenti">
            <div class="mobile-card-header">
              <strong>{{ a.data | date:'dd/MM/yyyy' }} ({{ a.ora_inizio }})</strong>
              <span class="badge" [ngClass]="'badge-' + a.stato">{{ a.stato }}</span>
            </div>
            <div class="mobile-card-body">
              <p><strong>Titolo:</strong> {{ a.titolo }}</p>
              <p *ngIf="a.cognome"><strong>Cliente:</strong> {{ a.cognome }} {{ a.nome }}</p>
              <p *ngIf="a.luogo"><strong>Luogo:</strong> {{ a.luogo }}</p>
              <p *ngIf="a.descrizione"><strong>Note:</strong> {{ a.descrizione }}</p>
            </div>
            <div class="mobile-card-footer">
              <button class="btn-sm" (click)="editApp(a)" style="width: auto; padding: 6px 12px;">Modifica</button>
              <button class="btn-sm btn-danger" (click)="deleteApp(a.id!)" style="width: auto; padding: 6px 12px;">Elimina</button>
            </div>
          </div>
          <div class="text-center" *ngIf="filteredAppuntamenti.length === 0">Nessun appuntamento trovato.</div>
        </div>
      </div>

      <!-- Modal Crea / Modifica Appuntamento -->
      <div class="modal-backdrop" *ngIf="showModal">
        <div class="modal-card">
          <h2>{{ editingId ? 'Modifica Appuntamento' : 'Nuovo Appuntamento' }}</h2>
          <form (ngSubmit)="saveApp()">
            <div class="form-group">
              <label>Titolo *</label>
              <input type="text" [(ngModel)]="currentApp.titolo" name="titolo" required placeholder="Es. Sopralluogo per preventivo tinteggiatura" />
            </div>

            <div class="form-row">
              <div class="form-group">
                <label>Data *</label>
                <input type="date" [(ngModel)]="currentApp.data" name="data" required />
              </div>
              <div class="form-group">
                <label>Ora Inizio *</label>
                <input type="time" [(ngModel)]="currentApp.ora_inizio" name="ora_inizio" required />
              </div>
              <div class="form-group">
                <label>Ora Fine *</label>
                <input type="time" [(ngModel)]="currentApp.ora_fine" name="ora_fine" required />
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label>Cliente</label>
                <select [(ngModel)]="currentApp.cliente_id" name="cliente_id">
                  <option [ngValue]="undefined">Seleziona cliente...</option>
                  <option *ngFor="let c of clienti" [ngValue]="c.id">{{ c.cognome }} {{ c.nome }}</option>
                </select>
              </div>
              <div class="form-group">
                <label>Stato</label>
                <select [(ngModel)]="currentApp.stato" name="stato">
                  <option value="programmato">Programmato</option>
                  <option value="completato">Completato</option>
                  <option value="annullato">Annullato</option>
                </select>
              </div>
            </div>

            <div class="form-group">
              <label>Luogo / Indirizzo Cantiere</label>
              <input type="text" [(ngModel)]="currentApp.luogo" name="luogo" placeholder="Es. Via Roma 15, Brescia" />
            </div>

            <div class="form-group">
              <label>Note / Dettagli</label>
              <textarea [(ngModel)]="currentApp.descrizione" name="descrizione" rows="3" placeholder="Es. Portare mazzetta colori Ral 9010 e misuratore laser..."></textarea>
            </div>

            <div class="modal-actions">
              <button type="button" class="btn-secondary" (click)="closeModal()">Annulla</button>
              <button type="submit" class="btn-primary">Salva Appuntamento</button>
            </div>
          </form>
        </div>
      </div>

    </div>
  `,
  styles: [`
    .page-container { max-width: 1280px; margin: 0 auto; }
    .header-flex { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; flex-wrap: wrap; gap: 16px; }
    .actions-group { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
    
    h1 { margin: 0; color: var(--text-main); font-size: 28px; font-weight: 800; letter-spacing: -0.5px; }
    .subtitle { color: var(--text-muted); margin: 4px 0 0; font-size: 14px; }

    /* View Toggle Buttons */
    .view-toggle {
      display: flex;
      background: var(--surface);
      border: 1px solid var(--border);
      padding: 4px;
      border-radius: 12px;
      box-shadow: var(--shadow);
    }
    .toggle-btn {
      display: flex;
      align-items: center;
      gap: 6px;
      border: none;
      background: none;
      padding: 8px 16px;
      border-radius: 8px;
      font-size: 13px;
      font-weight: 600;
      color: var(--text-muted);
      cursor: pointer;
      transition: all 0.2s;
    }
    .toggle-btn.active {
      background: var(--primary);
      color: white;
      box-shadow: 0 2px 8px rgba(200, 90, 50, 0.3);
    }
    .toggle-btn .material-icons { font-size: 18px; }

    /* Calendar Component Styles */
    .calendar-view {
      padding: 24px;
    }
    .calendar-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;
    }
    .month-nav {
      display: flex;
      align-items: center;
      gap: 16px;
    }
    .month-nav h2 {
      margin: 0;
      font-size: 22px;
      font-weight: 800;
      color: var(--text-main);
      text-transform: capitalize;
    }
    .btn-nav {
      background: var(--bg-main);
      border: 1px solid var(--border);
      border-radius: 50%;
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: var(--text-main);
      transition: all 0.2s;
    }
    .btn-nav:hover { background: var(--border); }
    .btn-today {
      background: var(--bg-main);
      border: 1px solid var(--border);
      padding: 8px 16px;
      border-radius: 20px;
      font-weight: 700;
      font-size: 13px;
      color: var(--text-main);
      cursor: pointer;
      transition: all 0.2s;
    }
    .btn-today:hover { border-color: var(--primary); color: var(--primary); }

    /* Grid */
    .calendar-grid {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      gap: 1px;
      background: var(--border);
      border: 1px solid var(--border);
      border-radius: 12px;
      overflow: hidden;
    }
    .weekday-header {
      background: var(--bg-main);
      color: var(--text-muted);
      font-weight: 700;
      font-size: 12px;
      text-transform: uppercase;
      text-align: center;
      padding: 12px 6px;
    }

    .calendar-day-cell {
      background: var(--surface);
      min-height: 110px;
      padding: 8px;
      display: flex;
      flex-direction: column;
      gap: 6px;
      cursor: pointer;
      transition: background 0.15s;
    }
    .calendar-day-cell:hover {
      background: var(--surface-hover);
    }
    .calendar-day-cell.other-month {
      opacity: 0.35;
      background: var(--bg-main);
    }
    .calendar-day-cell.is-today {
      background: var(--primary-light);
    }
    .calendar-day-cell.selected-day {
      border: 2px solid var(--primary);
    }

    .day-number-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .day-number {
      font-size: 14px;
      font-weight: 800;
      color: var(--text-main);
    }
    .is-today .day-number {
      color: var(--primary);
    }
    .btn-quick-add {
      background: none;
      border: none;
      color: var(--text-muted);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 2px;
      border-radius: 4px;
      opacity: 0;
      transition: opacity 0.2s;
    }
    .calendar-day-cell:hover .btn-quick-add { opacity: 1; }
    .btn-quick-add:hover { background: var(--border); color: var(--primary); }
    .btn-quick-add .material-icons { font-size: 16px; }

    .day-events-list {
      display: flex;
      flex-direction: column;
      gap: 4px;
      overflow-y: auto;
      max-height: 80px;
    }

    .event-chip {
      background: var(--bg-main);
      border-left: 3px solid var(--primary);
      padding: 3px 6px;
      border-radius: 4px;
      font-size: 11px;
      display: flex;
      align-items: center;
      gap: 4px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      transition: transform 0.15s;
    }
    .event-chip:hover { transform: scale(1.02); }
    .event-chip.chip-completato { border-left-color: var(--success); }
    .event-chip.chip-annullato { border-left-color: var(--danger); text-decoration: line-through; opacity: 0.6; }

    .event-time { font-weight: 700; color: var(--text-main); }
    .event-title { overflow: hidden; text-overflow: ellipsis; color: var(--text-main); }

    /* Selected Day Summary Drawer */
    .selected-day-panel {
      margin-top: 28px;
      padding-top: 20px;
      border-top: 1px solid var(--border);
    }
    .panel-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
      flex-wrap: wrap;
      gap: 12px;
    }
    .panel-header h3 { margin: 0; font-size: 18px; font-weight: 800; color: var(--text-main); text-transform: capitalize; }
    .btn-sm-add { padding: 8px 14px; font-size: 12px; }

    .events-cards-list {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    .event-detail-card {
      background: var(--bg-main);
      border: 1px solid var(--border);
      border-radius: 12px;
      padding: 16px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
      flex-wrap: wrap;
    }
    .ev-time-badge {
      display: flex;
      align-items: center;
      gap: 6px;
      background: var(--surface);
      border: 1px solid var(--border);
      padding: 6px 12px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 700;
      color: var(--primary);
    }
    .ev-time-badge .material-icons { font-size: 16px; }
    .ev-main-info { flex: 1; }
    .ev-main-info h4 { margin: 0 0 4px 0; font-size: 16px; font-weight: 700; color: var(--text-main); }
    .ev-main-info p { margin: 2px 0; font-size: 13px; color: var(--text-muted); }
    .ev-desc { font-style: italic; }
    .ev-actions { display: flex; align-items: center; gap: 8px; }

    .empty-msg { color: var(--text-muted); font-size: 14px; font-style: italic; }

    /* List View Styles */
    .search-bar { display: flex; align-items: center; gap: 12px; padding: 12px 20px; margin-bottom: 24px; border-radius: 12px; }
    .search-bar .search-icon { color: var(--text-muted); }
    .search-bar input { border: none; background: transparent; font-size: 15px; width: 100%; color: var(--text-main); outline: none; }
    .btn-clear { background: var(--border); border: none; padding: 4px 10px; border-radius: 6px; cursor: pointer; font-size: 12px; color: var(--text-muted); }

    .table-card { padding: 0; overflow: hidden; border-radius: 12px; border: 1px solid var(--border); }
    .data-table { width: 100%; border-collapse: collapse; text-align: left; }
    .data-table th, .data-table td { padding: 16px 20px; border-bottom: 1px solid var(--border); font-size: 14px; }
    .data-table th { background: var(--bg-main); color: var(--text-muted); font-weight: 600; text-transform: uppercase; font-size: 12px; letter-spacing: 0.5px; }
    .data-table tbody tr { transition: background 0.15s; }
    .data-table tbody tr:hover { background: var(--bg-main); }

    .btn-sm { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 6px; border: 1px solid var(--border); background: var(--surface); color: var(--text-muted); cursor: pointer; transition: all 0.2s; margin-right: 6px; }
    .btn-sm:hover { background: var(--bg-main); color: var(--text-main); }
    .btn-danger { background: rgba(239, 68, 68, 0.1); border-color: rgba(239, 68, 68, 0.2); color: var(--danger); }
    .btn-danger:hover { background: var(--danger); color: white; }

    .text-center { text-align: center; color: var(--text-muted); padding: 40px !important; }

    /* Mobile Card List */
    .mobile-only { display: none !important; }
    .desktop-only { display: block !important; }
    .mobile-card-list { display: flex; flex-direction: column; gap: 12px; }
    .mobile-item-card { padding: 16px; display: flex; flex-direction: column; gap: 10px; }
    .mobile-card-header { display: flex; justify-content: space-between; align-items: center; }
    .mobile-card-body p { margin: 4px 0; font-size: 13px; color: var(--text-muted); }
    .mobile-card-footer { display: flex; gap: 8px; margin-top: 8px; }

    /* Modal */
    .modal-backdrop { 
      position: fixed; 
      top: 0; 
      left: 0; 
      right: 0;
      bottom: 0;
      width: 100vw; 
      height: 100vh; 
      box-sizing: border-box;
      background: rgba(15, 23, 42, 0.65); 
      display: flex; 
      justify-content: center; 
      align-items: center; 
      z-index: 9999; 
    }
    .modal-card { 
      background: var(--surface); 
      padding: 32px; 
      border-radius: 16px; 
      width: 100%; 
      max-width: 600px; 
      box-shadow: var(--shadow-lg); 
      border: 1px solid var(--border); 
      box-sizing: border-box;
    }
    .modal-card h2 { margin-top: 0; margin-bottom: 24px; color: var(--text-main); font-size: 20px; font-weight: 700; }
    .form-row { display: flex; gap: 16px; }
    .form-group { margin-bottom: 18px; flex: 1; }
    label { display: block; margin-bottom: 6px; font-weight: 600; font-size: 13px; color: var(--text-main); }
    input, select, textarea { width: 100%; padding: 11px 14px; border: 1px solid var(--border); border-radius: 8px; font-size: 14px; box-sizing: border-box; background: var(--bg-main); transition: all 0.2s; color: var(--text-main); }
    input:focus, select:focus, textarea:focus { border-color: var(--primary); outline: none; background: var(--surface); box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15); }
    .modal-actions { display: flex; justify-content: flex-end; gap: 14px; margin-top: 28px; }

    @media (max-width: 768px) {
      .desktop-only { display: none !important; }
      .mobile-only { display: flex !important; }
      .calendar-day-cell { min-height: 70px; padding: 4px; }
      .event-chip .event-title { display: none; }
      .weekday-header { font-size: 10px; padding: 8px 2px; }

      .modal-backdrop {
        padding: 16px !important;
        align-items: center !important;
        overflow-y: auto !important;
      }
      .modal-card {
        padding: 22px 18px !important;
        max-height: none !important;
        overflow: visible !important;
        border-radius: 20px !important;
        width: 100% !important;
        box-sizing: border-box !important;
        margin: auto !important;
      }
      input, select, textarea {
        font-size: 16px !important; /* Previene auto-zoom e disallineamenti mobile */
      }
      .form-row {
        flex-direction: column !important;
        gap: 0 !important;
      }
      .modal-actions {
        flex-direction: column-reverse;
        gap: 14px;
        margin-top: 22px;
      }
      .modal-actions button {
        width: 100%;
        margin: 0;
      }
    }
  `]
})
export class AppuntamentiComponent implements OnInit {
  appuntamenti: Appuntamento[] = [];
  clienti: Cliente[] = [];
  searchTerm = '';
  showModal = false;
  editingId: number | null = null;
  currentApp: Appuntamento = { titolo: '', data: '', ora_inizio: '', ora_fine: '', stato: 'programmato' };

  // Calendar State
  viewMode: 'calendar' | 'list' = 'calendar';
  currentMonthDate: Date = new Date();
  weekDays = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];
  calendarDays: CalendarDay[] = [];
  selectedDay: CalendarDay | null = null;

  constructor(
    private apiService: ApiService,
    private toastService: ToastService,
    private confirmService: ConfirmService
  ) {}

  ngOnInit() {
    this.loadAppuntamenti();
    this.loadClienti();
  }

  get filteredAppuntamenti(): Appuntamento[] {
    if (!this.searchTerm.trim()) return this.appuntamenti;
    const term = this.searchTerm.toLowerCase();
    return this.appuntamenti.filter(a =>
      a.titolo?.toLowerCase().includes(term) ||
      a.nome?.toLowerCase().includes(term) ||
      a.cognome?.toLowerCase().includes(term) ||
      a.luogo?.toLowerCase().includes(term) ||
      a.stato?.toLowerCase().includes(term)
    );
  }

  loadAppuntamenti() {
    this.apiService.getAppuntamenti().subscribe({
      next: (data) => {
        this.appuntamenti = data;
        this.generateCalendar();
      },
      error: (err) => console.error('Errore caricamento appuntamenti', err)
    });
  }

  loadClienti() {
    this.apiService.getClienti().subscribe({
      next: (data) => this.clienti = data,
      error: (err) => console.error('Errore caricamento clienti', err)
    });
  }

  // CALENDAR GENERATION LOGIC
  generateCalendar() {
    const year = this.currentMonthDate.getFullYear();
    const month = this.currentMonthDate.getMonth();

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);

    // Day of week offset (Monday = 0, Sunday = 6)
    let firstDayIndex = firstDayOfMonth.getDay() - 1;
    if (firstDayIndex < 0) firstDayIndex = 6;

    const days: CalendarDay[] = [];
    const todayStr = this.formatDateStr(new Date());

    // Previous month trailing days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = firstDayIndex - 1; i >= 0; i--) {
      const d = new Date(year, month - 1, prevMonthLastDay - i);
      const dateStr = this.formatDateStr(d);
      days.push({
        date: d,
        isCurrentMonth: false,
        isToday: dateStr === todayStr,
        dateString: dateStr,
        events: this.getEventsForDate(dateStr)
      });
    }

    // Current month days
    for (let i = 1; i <= lastDayOfMonth.getDate(); i++) {
      const d = new Date(year, month, i);
      const dateStr = this.formatDateStr(d);
      days.push({
        date: d,
        isCurrentMonth: true,
        isToday: dateStr === todayStr,
        dateString: dateStr,
        events: this.getEventsForDate(dateStr)
      });
    }

    // Next month padding days to complete 35 or 42 grid cells
    const remaining = (7 - (days.length % 7)) % 7;
    for (let i = 1; i <= remaining; i++) {
      const d = new Date(year, month + 1, i);
      const dateStr = this.formatDateStr(d);
      days.push({
        date: d,
        isCurrentMonth: false,
        isToday: dateStr === todayStr,
        dateString: dateStr,
        events: this.getEventsForDate(dateStr)
      });
    }

    this.calendarDays = days;

    // Select today or first day
    if (!this.selectedDay) {
      const todayCell = days.find(d => d.isToday && d.isCurrentMonth);
      this.selectedDay = todayCell || days.find(d => d.isCurrentMonth) || days[0];
    } else {
      // Re-find selected day in new month
      const updated = days.find(d => d.dateString === this.selectedDay?.dateString);
      if (updated) this.selectedDay = updated;
    }
  }

  getEventsForDate(dateStr: string): Appuntamento[] {
    return this.appuntamenti.filter(a => {
      if (!a.data) return false;
      const appDateStr = this.formatDateStr(new Date(a.data));
      return appDateStr === dateStr;
    });
  }

  formatDateStr(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  prevMonth() {
    this.currentMonthDate = new Date(this.currentMonthDate.getFullYear(), this.currentMonthDate.getMonth() - 1, 1);
    this.generateCalendar();
  }

  nextMonth() {
    this.currentMonthDate = new Date(this.currentMonthDate.getFullYear(), this.currentMonthDate.getMonth() + 1, 1);
    this.generateCalendar();
  }

  goToToday() {
    this.currentMonthDate = new Date();
    this.generateCalendar();
    const todayStr = this.formatDateStr(new Date());
    const todayCell = this.calendarDays.find(d => d.dateString === todayStr);
    if (todayCell) this.selectedDay = todayCell;
  }

  selectDay(day: CalendarDay) {
    this.selectedDay = day;
  }

  // MODAL HANDLERS
  openModal() {
    this.editingId = null;
    const dateStr = this.selectedDay ? this.selectedDay.dateString : this.formatDateStr(new Date());
    this.currentApp = { 
      titolo: '', 
      data: dateStr, 
      ora_inizio: '08:30', 
      ora_fine: '10:00', 
      stato: 'programmato' 
    };
    this.showModal = true;
  }

  openModalForDate(dateStr: string, ev?: Event) {
    if (ev) ev.stopPropagation();
    this.editingId = null;
    this.currentApp = { 
      titolo: '', 
      data: dateStr, 
      ora_inizio: '08:30', 
      ora_fine: '10:00', 
      stato: 'programmato' 
    };
    this.showModal = true;
  }

  editApp(a: Appuntamento) {
    this.editingId = a.id!;
    this.currentApp = { ...a };
    if (this.currentApp.data) {
      this.currentApp.data = this.formatDateStr(new Date(this.currentApp.data));
    }
    this.showModal = true;
  }

  closeModal() {
    this.showModal = false;
  }

  saveApp() {
    if (!this.currentApp.titolo || !this.currentApp.data || !this.currentApp.ora_inizio || !this.currentApp.ora_fine) {
      this.toastService.warning('Compila tutti i campi obbligatori');
      return;
    }

    if (this.editingId) {
      this.apiService.updateAppuntamento(this.editingId, this.currentApp).subscribe({
        next: () => {
          this.toastService.success('Appuntamento aggiornato con successo');
          this.loadAppuntamenti();
          this.closeModal();
        },
        error: (err) => this.toastService.error(err.error?.error || 'Errore durante aggiornamento')
      });
    } else {
      this.apiService.createAppuntamento(this.currentApp).subscribe({
        next: () => {
          this.toastService.success('Appuntamento creato con successo');
          this.loadAppuntamenti();
          this.closeModal();
        },
        error: (err) => this.toastService.error(err.error?.error || 'Errore durante creazione')
      });
    }
  }

  async deleteApp(id: number) {
    const confirmed = await this.confirmService.confirm(
      'Elimina Appuntamento',
      'Sei sicuro di voler eliminare questo appuntamento?',
      'Elimina',
      'Annulla',
      true
    );
    if (confirmed) {
      this.apiService.deleteAppuntamento(id).subscribe({
        next: () => {
          this.toastService.success('Appuntamento eliminato');
          this.loadAppuntamenti();
        },
        error: (err) => this.toastService.error(err.error?.error || 'Errore eliminazione')
      });
    }
  }
}
